import { differenceInCalendarDays, getDate } from 'date-fns';
import { policyTypes as PolicyType } from '@ourbranch/policy-types';
import { alabamaLogicInstallmentFeeStates, fixedHomeInstallmentFeeStates, localPremiumTaxStates, statePremiumTaxStates, zeroPremiumTaxCities, shortRateFeeStates, getFinancialResponsibilityTier, directAffinityCodes, altCreditAlgorithmStates, standaloneRentersStates as StandaloneRentersStates, condoStates as CondoStates, flatInstallmentFee } from '@ourbranch/lookups';
import { setDownPayToPSCAffinities } from '@ourbranch/be-constants';
import { getTodayStr } from '@ourbranch/date-helpers';
const installmentCountByTypeAndState = {
    A: {
        LA: 5,
        NH: 5
    },
    H: {
        LA: 11,
        NH: 11
    }
};
const nonBixNonThreeDollarInstallmentFeeStates = {
    IL: true,
    MO: true,
    TX: true
};
const getInventoryDiscount = ({ policyType, state, inBundle = false, isBix }) => {
    if (policyType === PolicyType.Auto || policyType === PolicyType.ARBundle) {
        if (state === 'TX') {
            // auto and auto/renters bundle in TX
            return 0;
        }
        if (state === 'IL' || (state === 'MO' && !isBix)) {
            // auto and auto/renters bundle in IL & MO GSNIC
            return inBundle ? 15 : 20;
        }
    }
    if (state === 'IL') {
        // home, IL
        return 50;
    }
    // home and home/auto bundle, elsewhere
    return inBundle ? 55 : 65;
};
export const FeeType = {
    Policy: 'policy',
    Imagery: 'imagery',
    Paper: 'paper',
    Installment: 'installment',
    Cancellation: 'cancellation',
    Reinstatement: 'reinstatement',
    AutoTheft: 'auto_theft',
    PoliceTraining: 'police_training',
    RuralFireDept: 'rural_fire_dept',
    FairPlan: 'fair_plan',
    AutoTheftPrevention: 'auto_theft_prevention',
    AutoCrimePrevention: 'auto_crime_prevention',
    MCCA: 'mcca'
};
/**
 *
 * @param {string} feeType, cant be A for autoPolicy fees or H for HomePolicyFees
 * @param {object} feeOptions: { policyType, inBundle, state }
 */
export function generateFee(feeType, feeOptions) {
    let feeDescription;
    let feeAmount = 0;
    let deviceCost;
    const { policyType, state, inBundle, isBix, policyLimitPIPME, premium, effectiveDate, billingDayOfMonth, isMegaDownPay, cancelCode, term } = feeOptions;
    const effectiveDateInt = Number(String(effectiveDate).replace(/-/g, ''));
    let paymentCount = policyType === PolicyType.Auto
        ? installmentCountByTypeAndState[policyType]?.[state] || 6
        : installmentCountByTypeAndState[policyType]?.[state] || 12;
    if (isMegaDownPay || (term === 1 && billingDayOfMonth === Number(effectiveDate?.split('-')[2]))) {
        paymentCount -= 1;
    }
    let mccaBaseFee = 43;
    let mccaOtherFee = 0;
    if (effectiveDateInt >= 20240701) {
        mccaBaseFee = 45;
        mccaOtherFee = 10;
    }
    else if (effectiveDateInt >= 20230701) {
        mccaBaseFee = 61;
        mccaOtherFee = 24;
    }
    switch (feeType.toLowerCase()) {
        case 'policy':
            feeDescription = 'Policy';
            if ((state === 'MO' && !isBix) || (state === 'TX' && !isBix) || (state === 'AL' && policyType === 'H')) {
                feeAmount = 0;
            }
            else {
                feeAmount = 5;
            }
            break;
        case 'paper':
            feeDescription = 'Paper Documentation';
            feeAmount = state === 'TX' && !isBix ? 25 : 0;
            break;
        case 'imagery': {
            feeDescription = 'Imagery';
            feeAmount =
                policyType === PolicyType.HABundle
                    ? getInventoryDiscount({ policyType: 'H', state, inBundle: true, isBix }) +
                        getInventoryDiscount({ policyType: 'A', state, inBundle: true, isBix })
                    : getInventoryDiscount({ policyType, state, inBundle, isBix });
            break;
        }
        case 'installment':
            feeDescription = 'Installment';
            if (!isBix && nonBixNonThreeDollarInstallmentFeeStates[state]) {
                feeAmount = feeOptions.paymentMethod === 'E' ? 1 * paymentCount : 4 * paymentCount;
            }
            else {
                feeAmount = flatInstallmentFee * paymentCount; // flat $3/installment for BIX and new GSNIC
            }
            break;
        case 'cancellation':
            feeDescription = 'Cancellation';
            if (state === 'SC') {
                feeAmount = 10;
            }
            else if (state === 'AK') {
                // For Alaska, the cancellation fee is 7.5% of the unearned premium IF the cancellation reason is included
                // in the list below. If the cancellation reason is not included in the list below, no cancellation fee should apply
                if (['CNDB', 'CNDC', 'CNNO', 'CNDA', 'CNPR'].includes(cancelCode)) {
                    const effectiveDateJS = new Date(effectiveDate);
                    const policyEndDate = new Date(new Date(effectiveDate).setMonth(new Date(effectiveDate).getMonth() + (policyType === 'A' ? 6 : 12)) - 1);
                    const policyDaysRemaining = differenceInCalendarDays(policyEndDate, new Date());
                    const perDayPremium = getDailyRate(premium, policyEndDate, effectiveDateJS);
                    feeAmount = roundTwoDecimals(perDayPremium * policyDaysRemaining * 0.075);
                }
            }
            else if (state !== 'ND') {
                feeAmount = 35;
            }
            break;
        case 'short_rate':
            feeDescription = 'Short Rate VA Cancellation Amount';
            feeAmount = (premium || 1) * 0.1; // need to know premium here to figure out unearned premium
            break;
        case 'reinstatement':
            feeDescription = 'Reinstatement';
            if ((state === 'MI' && policyType === 'A') || (state === 'SD' && policyType === 'H') || state === 'ND') {
                feeAmount = 0;
            }
            else {
                feeAmount = 5;
            }
            break;
        case 'police_training':
            feeDescription = 'State Police Training and Academy Fund Assessment';
            feeAmount =
                2 *
                    (Array.isArray(feeOptions.cars)
                        ? feeOptions.cars.filter((c) => c.deductibleComprehensive !== 'NONE').length
                        : 1);
            break;
        case 'auto_theft':
            feeDescription = 'Auto Theft Authority';
            feeAmount = 0.5 * (Array.isArray(feeOptions.cars) ? feeOptions.cars.length : 1);
            break;
        case 'mcca':
            feeDescription = 'MCCA Full Recoupment Fee';
            feeAmount =
                policyLimitPIPME && policyLimitPIPME === 'UNLIMITED'
                    ? mccaBaseFee * (Array.isArray(feeOptions.cars) ? feeOptions.cars.length : 1)
                    : mccaOtherFee * (Array.isArray(feeOptions.cars) ? feeOptions.cars.length : 1);
            break;
        case 'rural_fire_dept':
            feeDescription = 'Rural Volunteer Fire Department Assistance Program Recoupment Fee';
            feeAmount = 0;
            break;
        case 'fair_plan':
            feeDescription = 'FAIR Plan Surcharge';
            feeAmount = 0;
            break;
        case 'home_hazard':
            feeDescription = 'Home Hazard Mitigation Fee';
            feeAmount = effectiveDateInt >= 20220301 ? 2 : 0;
            break;
        case 'auto_theft_prevention':
            feeDescription = 'Auto Theft Prevention';
            deviceCost = state === 'TX' && policyType !== 'H' ? 2 : 0.5;
            feeAmount =
                deviceCost *
                    (Array.isArray(feeOptions.cars)
                        ? feeOptions.cars.filter((c) => c.deductibleComprehensive !== 'NONE').length
                        : 1);
            break;
        case 'auto_crime_prevention':
            feeDescription = 'Motor Vehicle Crime Prevention';
            feeAmount = 2 * (Array.isArray(feeOptions.cars) ? feeOptions.cars.length : 1);
            break;
        default:
            break;
    }
    return { description: feeDescription, amount: feeAmount };
} // generateFee
function roundTwoDecimals(num) {
    return Math.round(num * 1e2) / 1e2;
}
function getPremiumBreakdownForFees({ coverages, premium, policyType, premiumBreakdown }) {
    let homePremium = 0;
    let sppPremium = 0;
    let umbrellaPremium = 0;
    let autoPremium = 0;
    let rentersPremium = 0;
    let condoPremium = 0;
    // do we need this logic for coverages? we are already calculating the spp on bind request to accounts
    if (coverages) {
        if (policyType === 'A') {
            rentersPremium = coverages
                .filter((c) => c.type === 'renters_total')
                .reduce((acc, c) => {
                return acc + c.amount;
            }, 0);
            autoPremium = premium - rentersPremium;
        }
        // not sure if we want to include condo here
        if (policyType === 'H') {
            sppPremium = (coverages.find((x) => x.type.toLowerCase() === 'spp_total') || {}).amount || 0;
            umbrellaPremium =
                (coverages.find((x) => x.type.toLowerCase() === 'personal_umbrella_liability_total') || {}).amount || 0;
            homePremium = premium - (sppPremium + umbrellaPremium);
        }
    }
    else if (premiumBreakdown) {
        condoPremium = premiumBreakdown.condo || 0;
        homePremium = premiumBreakdown.home || 0;
        sppPremium = premiumBreakdown.spp || 0;
        umbrellaPremium = premiumBreakdown.umbrella || 0;
        autoPremium = premiumBreakdown.car || 0;
        rentersPremium = premiumBreakdown.renters || 0;
    }
    else {
        throw new Error('Did not receive coverages or premium breakdown as part of local premium tax calculations.');
    }
    return {
        homePremium,
        sppPremium,
        umbrellaPremium,
        autoPremium,
        rentersPremium,
        condoPremium
    };
}
function getLocalTaxRates({ localPremiumTax }) {
    const retObj = {
        minimum: 0
    };
    const lookupCodes = [
        { name: 'home', code: 'KY01' },
        { name: 'spp', code: 'KY04' },
        { name: 'umbrella', code: 'KY02' },
        { name: 'auto', code: 'KY03' },
        { name: 'renters', code: 'KY01' },
        { name: 'condo', code: 'KY01' }
    ];
    const { minimumTax } = localPremiumTax;
    if (minimumTax && minimumTax !== 'N/A') {
        const minimumCandidate = Number(minimumTax.replace('$', ''));
        if (!Number.isNaN(minimumCandidate)) {
            retObj.minimum = minimumCandidate;
        }
    }
    for (const lookup of lookupCodes) {
        const { name, code } = lookup;
        const record = localPremiumTax?.lineOfBusinessDetails?.find((lob) => lob?.code === code);
        if (!record) {
            console.log(`could not find local premium tax (assuming not a taxable area) for ${name} with code ${code} in ${JSON.stringify(localPremiumTax, null, 2)}`);
            retObj[`${name}Percentage`] = 0;
        }
        else {
            const premiumTaxPercentageCandidate = record?.taxValue === 'NONE' ? 0 : parseInt(record?.taxValue, 10);
            if (Number.isNaN(premiumTaxPercentageCandidate)) {
                console.log(`premium tax candidate is NaN: ${record?.taxValue} / ${parseInt(record?.taxValue, 10)}`);
                throw new Error('Premium Tax is not a number.');
            }
            retObj[`${name}Percentage`] = premiumTaxPercentageCandidate / 100;
        }
    } // for each thing we need a lookup
    return retObj;
}
function calculatePremiumTax({ premium, premiumPart, nonPremiumExtraTotal, percentage, minimum }) {
    // if premiumPart is 0, then no tax owed:
    if (premiumPart === 0) {
        return 0;
    }
    // spread nonPremiumExtraTotal pro-rata based upon the premium part of the total premium
    // and respect the minimum
    const calcValue = (premiumPart + (nonPremiumExtraTotal * premiumPart) / premium) * percentage;
    if (Number.isNaN(calcValue)) {
        console.log('calcValue was zero, so returning premium tax of 0');
        return 0;
    }
    console.log(`calculate premium tax: ${minimum}, ${calcValue}; yielding: ${minimum > calcValue ? minimum : calcValue}`);
    return minimum > calcValue ? minimum : calcValue;
}
/**
 *
 * @param {string} state, state in uppercase
 * @param {string} policyType, H or A
 * @param {object} fees: { amount, appliedDate, description, type }
 * @param {float} premium
 * @param {float} downPayment
 * @param {object} quote: quote object from an offer
 */
export function adjustFees({ fees, state, policyType, premium, surplusContribution, localPremiumTax, coverages, premiumBreakdown, quote, optionType }) {
    console.log(`adjustFees called with state ${state}`);
    // At this point, this function only implements the Alabama fee logic,
    // premium taxes, and short rating in VA cancels
    if (shortRateFeeStates[state]) {
        const shortRateFee = fees.find((f) => f.type === 'short_rate');
        if (shortRateFee) {
            // we have a short rate fee stubbed out with the premium on the full policy
            // now premium is the earned amount, so subtracting the two will give us the unearned amount
            // and the short rate fee is 10% of that
            shortRateFee.amount = roundTwoDecimals((shortRateFee.amount - (premium || 0)) * 0.1);
        }
    } // short rating
    if (localPremiumTaxStates[state]) {
        // first, set up the local premium tax fee:
        if (!fees.find((f) => f.type === 'local_premium_tax')) {
            fees.push({
                type: 'local_premium_tax',
                description: `${state} Municipal Tax`,
                appliedDate: getTodayStr(state),
                amount: 0
            });
        }
        const taxFee = fees.find((f) => f.type === 'local_premium_tax');
        // second, update it based upon the various variables:
        // non-tax fee total
        const feeTotal = fees
            .filter((fee) => !['local_premium_tax', 'state_premium_tax'].includes(fee?.type))
            .reduce((acc, curr) => acc + curr.amount, 0);
        const nonPremiumExtraTotal = (surplusContribution || 0) + (feeTotal || 0);
        // now we build up how much of each type of premium we have here:
        const premiumBreakdownResult = getPremiumBreakdownForFees({
            coverages,
            premium,
            policyType,
            premiumBreakdown
        });
        const { homePremium, sppPremium, umbrellaPremium, autoPremium, rentersPremium, condoPremium } = premiumBreakdownResult;
        console.log(`premium breakdown for fees: ${JSON.stringify(premiumBreakdown, null, 2)}; coverages: ${JSON.stringify(coverages, null, 2)}`);
        // pull out address:
        const address = quote.submittedAddress ||
            quote.correctedAddress ||
            quote.home?.homeLocation ||
            quote.condo?.condoLocation ||
            quote.rentersCoverage?.rentersLocation ||
            quote.cars?.[0]?.garageLocation;
        const zeroPremiumTaxCitiesInState = zeroPremiumTaxCities[state];
        const zeroPremiumTaxHere = zeroPremiumTaxCitiesInState
            ? !!zeroPremiumTaxCitiesInState[String(address.city).toUpperCase()]
            : false;
        // local premium tax:
        if (!localPremiumTax && !zeroPremiumTaxHere) {
            throw new Error(`Could not find localPremiumTax node, and ${address.city} is not in zeroPremiumTaxCities.`);
        }
        if (localPremiumTax) {
            const localTax = getLocalTaxRates({ localPremiumTax });
            console.log(`localTax: ${JSON.stringify(localTax, null, 2)}`);
            const { homePercentage, sppPercentage, umbrellaPercentage, autoPercentage, rentersPercentage, minimum, condoPercentage } = localTax;
            const homeTax = calculatePremiumTax({
                premium,
                premiumPart: homePremium,
                nonPremiumExtraTotal,
                percentage: homePercentage,
                minimum
            });
            const condoTax = calculatePremiumTax({
                premium,
                premiumPart: condoPremium,
                nonPremiumExtraTotal,
                percentage: condoPercentage,
                minimum
            });
            const sppTax = calculatePremiumTax({
                premium,
                premiumPart: sppPremium,
                nonPremiumExtraTotal,
                percentage: sppPercentage,
                minimum
            });
            const umbrellaTax = calculatePremiumTax({
                premium,
                premiumPart: umbrellaPremium,
                nonPremiumExtraTotal,
                percentage: umbrellaPercentage,
                minimum
            });
            const autoTax = calculatePremiumTax({
                premium,
                premiumPart: autoPremium,
                nonPremiumExtraTotal,
                percentage: autoPercentage,
                minimum
            });
            const rentersTax = calculatePremiumTax({
                premium,
                premiumPart: rentersPremium,
                nonPremiumExtraTotal,
                percentage: rentersPercentage,
                minimum
            });
            const sumOfTaxes = homeTax + sppTax + umbrellaTax + autoTax + rentersTax + condoTax;
            console.log(`sum Of Taxes: ${sumOfTaxes}`);
            if (policyType === 'H') {
                taxFee.homeTax = homeTax;
                taxFee.sppTax = sppTax;
                taxFee.umbrellaTax = umbrellaTax;
            }
            if (policyType === 'A') {
                taxFee.autoTax = autoTax;
                taxFee.rentersTax = rentersTax;
            }
            if (policyType === 'R') {
                taxFee.sppTax = sppTax;
                taxFee.rentersTax = rentersTax;
            }
            if (policyType === 'C') {
                taxFee.condoTax = condoTax;
                taxFee.sppTax = sppTax;
                taxFee.umbrellaTax = umbrellaTax;
            }
            taxFee.amount = roundTwoDecimals(sumOfTaxes);
            taxFee.description = `${state} Municipal Tax${localPremiumTax?.territoryName ? ` - ${localPremiumTax.territoryName}` : ''}`;
        }
        else {
            // no local premium tax, and in a zero premium tax city, so we zero it out.
            taxFee.amount = 0;
            taxFee.description = `${address.city} has no local premium tax`;
        }
    } // local premium tax
    if (statePremiumTaxStates[state]) {
        // first, set up the local premium tax fee:
        if (!fees.find((f) => f.type === 'state_premium_tax')) {
            fees.push({
                type: 'state_premium_tax',
                description: `${state} Insurance Premium Surcharge`,
                appliedDate: getTodayStr(state),
                amount: 0
            });
        }
        const taxFee = fees.find((f) => f.type === 'state_premium_tax');
        // second, update it based upon the various variables:
        // non-tax fee total
        const feeTotal = fees
            .filter((fee) => !['local_premium_tax', 'state_premium_tax'].includes(fee?.type))
            .reduce((acc, curr) => acc + curr.amount, 0);
        const underlyingTotal = premium + (surplusContribution || 0) + (feeTotal || 0);
        // state premium tax:
        const premiumTaxPercentage = 0.018;
        taxFee.amount = roundTwoDecimals(underlyingTotal * premiumTaxPercentage);
    } // state premium tax
    // if at some future point this function needs to do more, please refactor!
    if (!alabamaLogicInstallmentFeeStates[state] || !Array.isArray(fees)) {
        return;
    }
    const installmentFee = fees.find((f) => f.type === 'installment');
    if (!installmentFee) {
        return;
    }
    const policyTypeMap = {
        [PolicyType.Auto]: 'auto',
        [PolicyType.Home]: 'home',
        [PolicyType.Renters]: 'renters',
        [PolicyType.Condo]: 'condo'
    };
    const feeTotal = fees.reduce((total, fee) => total + fee.amount, 0);
    const newDownPayment = getDownPayment({
        affinity: quote.global?.affinity,
        premium,
        policyType,
        optionType,
        state,
        quote,
        billingDay: quote.global?.[`${policyTypeMap[policyType]}BillingDayOfMonth`],
        paymentMethod: quote.global?.[`${policyType === PolicyType.Home ? 'homeowners' : policyTypeMap[policyType]}PaymentMethod`],
        total: premium + feeTotal + surplusContribution,
        effectiveDate: quote.global?.[`${policyTypeMap[policyType]}EffectiveDate`],
        fees,
        surplusContribution
    });
    const rate = policyType === 'A' ? 0.045 : 0.09;
    const paymentCount = policyType === 'A' ? 5 : 11; // don't count the downpayment
    const feeMin = 3;
    const altFeeCalc = roundTwoDecimals(Math.floor((((premium || 0) - (newDownPayment || 0)) * rate + 15) / paymentCount));
    console.log(`fee adjustment from ${installmentFee.amount} to greater of ${feeMin} or ${altFeeCalc} (${premium}, ${newDownPayment}, ${rate}, ${paymentCount})`);
    installmentFee.amount = (altFeeCalc < feeMin ? altFeeCalc : feeMin) * (paymentCount + 1);
}
/**
 *
 * @param {float} premium
 * @param {string} state, state in uppercase
 * @param {string} policyType, H or A
 *
 */
/**
 * Helper function to extract the first number from a BI limit string
 * Handles formats like "25/50/50" or "500 CSL"
 */
function extractFirstBILimitNumber(limitString) {
    if (!limitString) {
        return 0;
    }
    if (!Number.isNaN(Number(limitString))) {
        return Number(limitString);
    }
    // Extract first number before a slash or space
    const match = String(limitString).match(/^(\d+)(?:\/|\s|$)/);
    return match ? Number(match[1]) : 0;
}
export function getMinimumDownPayment({ affinity, premium, policyType, state, quote, optionType, surplusContribution, upfrontFeeTotal }) {
    const isAgency = quote.isAgentSold;
    const isPartner = !!quote.global.affinity && !quote.isAgentSold && !directAffinityCodes.includes(quote.global.affinity);
    const drivers = quote?.drivers?.length ? quote.drivers : quote.people;
    const primary = drivers.find((driver) => driver.isPrimary);
    const coApplicant = drivers.find((driver) => driver.isCoApplicant);
    const primaryInsuranceScore = primary?.insuranceScore?.autoTotal || primary?.insuranceScore?.total;
    const coAppInsuranceScore = coApplicant?.insuranceScore?.autoTotal || coApplicant?.insuranceScore?.total;
    const frTier = getFinancialResponsibilityTier(primaryInsuranceScore, coAppInsuranceScore, altCreditAlgorithmStates[state], state);
    const clean = !drivers.some((d) => d.nonUDRIncidentCount > 0 || d.hasUDR);
    const isBundle = ['HA', 'CA'].includes(optionType);
    const isDirect = !isAgency && !isPartner;
    const policyMonths = policyType === PolicyType.Auto ? 6 : 12;
    let minimum = 0;
    if (policyType === PolicyType.Auto) {
        const priorBILimit = extractFirstBILimitNumber(quote.global.priorIndividualBILimit);
        const currentBILimit = extractFirstBILimitNumber(quote.autoCoverage.policyLimitBIPD);
        // Use the minimum of the two values
        const biFirstNum = Math.min(priorBILimit, currentBILimit);
        console.log(`[get-minimum-down-payment] Running rules in ${state} with isDirect of ${isDirect} and isAgency of ${isAgency} and isPartner of ${isPartner} and policyMonths of ${policyMonths} and premium of ${premium} and frTier of ${frTier} and clean of ${clean} and biFirstNum of ${biFirstNum} (min of prior: ${priorBILimit}, current: ${currentBILimit})`);
        // Extract the first number from both prior and current BI limits
        // State regulations in MO require auto insurers to take a down pay of at least 1/6 of the premium
        if (state === 'MO') {
            minimum = premium / 6;
        }
        const amountToUse = premium + upfrontFeeTotal;
        // rules for everyone:
        const doNotUseInsuranceScore = ['NM'].includes(state);
        if (['AK', 'CA', 'MD'].includes(state)) {
            if (!clean || Number(biFirstNum) < 50000) {
                minimum = (3 * amountToUse) / policyMonths;
            }
            else if (!isAgency) {
                minimum = (2 * amountToUse) / policyMonths;
            }
        }
        else if (['SC'].includes(state)) {
            if (['A-1', 'B-1', 'C-1'].includes(frTier)) {
                minimum = (2 * amountToUse) / policyMonths;
            }
            else if (['D-1', 'E-1', 'F-1', 'G-1', 'H-1', 'I-1', 'J-1', 'K-1', 'L-1', 'M-1', 'N-1'].includes(frTier) &&
                clean &&
                Number(biFirstNum) >= 50000) {
                minimum = (2 * amountToUse) / policyMonths;
            }
            else {
                minimum = (3 * amountToUse) / policyMonths;
            }
        }
        else if (['KS'].includes(state)) {
            if (['A-1', 'B-1', 'C-1'].includes(frTier)) {
                if (isAgency) {
                    minimum = 0; // use regular rules
                }
                else {
                    minimum = (2 * amountToUse) / policyMonths;
                }
            }
            else if (['D-1', 'E-1', 'F-1', 'G-1', 'H-1', 'I-1', 'J-1', 'K-1', 'L-1', 'M-1', 'N-1'].includes(frTier) &&
                clean &&
                Number(biFirstNum) >= 50000) {
                minimum = (2 * amountToUse) / policyMonths;
            }
            else {
                minimum = (3 * amountToUse) / policyMonths;
            }
        }
        else if (['TX'].includes(state)) {
            // Keep the rules implemented in #21002 for TX
            if (isBundle && ['A-1', 'B-1', 'C-1', 'D-1', 'E-1'].includes(frTier) && Number(biFirstNum) >= 250000) {
                minimum = 0;
            }
            else if (isBundle &&
                ['A-1', 'B-1', 'C-1', 'D-1', 'E-1', 'F-1', 'G-1', 'H-1', 'I-1', 'J-1'].includes(frTier) &&
                Number(biFirstNum) >= 100000) {
                minimum = (0.5 * amountToUse) / policyMonths;
            }
            else if (isBundle &&
                ['A-1', 'B-1', 'C-1', 'D-1', 'E-1', 'F-1', 'G-1', 'H-1', 'I-1', 'J-1'].includes(frTier) &&
                Number(biFirstNum) >= 50000) {
                minimum = (1 * amountToUse) / policyMonths;
            }
            else if (['A-1', 'B-1', 'C-1', 'D-1', 'E-1', 'F-1', 'G-1', 'H-1', 'I-1', 'J-1'].includes(frTier) &&
                Number(biFirstNum) >= 50000) {
                minimum = (2 * amountToUse) / policyMonths;
            }
            else {
                minimum = (3 * amountToUse) / policyMonths;
            }
        }
        else {
            // Countrywide direct rules, last updated from issue #22404
            if ((doNotUseInsuranceScore || ['A-1', 'B-1', 'C-1', 'D-1', 'E-1'].includes(frTier)) &&
                Number(biFirstNum) >= 250000) {
                minimum = 0;
                // by affinity, generally set for "book rolls"
            }
            else if (setDownPayToPSCAffinities.includes(affinity)) {
                minimum = 0;
            }
            else if ((doNotUseInsuranceScore ||
                ['A-1', 'B-1', 'C-1', 'D-1', 'E-1', 'F-1', 'G-1', 'H-1', 'I-1', 'J-1'].includes(frTier)) &&
                Number(biFirstNum) >= 50000) {
                minimum = (0.5 * amountToUse) / policyMonths; // half a month
            }
            else if (!isBundle &&
                (doNotUseInsuranceScore || ['M-1', 'N-1', 'O-1', 'P-1', 'Q-1', 'R-1'].includes(frTier)) &&
                Number(biFirstNum) < 50000) {
                minimum = (2 * amountToUse) / policyMonths; // two months
            }
            else {
                minimum = amountToUse / policyMonths; // one month
            }
        }
    } // special downpay rules for auto
    console.log(`[get-minimum-down-payment] minimum is ${minimum} with upfrontFeeTotal of ${upfrontFeeTotal} and ${surplusContribution}`);
    // as per GitHub issue 17847, we add fee total and surplus contribution to the minimum:
    return roundTwoDecimals(minimum + (surplusContribution || 0));
}
export function getDailyRate(total, endDate, effectiveDate) {
    const days = differenceInCalendarDays(endDate, effectiveDate) + 1;
    console.log('Get daily rate', total, endDate, effectiveDate, days);
    return total / days;
}
export function getDownPayment({ affinity, premium, policyType, state, quote, billingDay, total, effectiveDate: effectiveDateIn, surplusContribution, fees, isMegaDownPay, optionType }) {
    let downPayment;
    const isTwelveMonthPolicyTerm = policyType === PolicyType.Home ||
        (policyType === PolicyType.Renters && StandaloneRentersStates[state]) ||
        (policyType === PolicyType.Condo && CondoStates[state]);
    const installmentCount = isTwelveMonthPolicyTerm ? 12 : 6;
    // fixed installment fee states just lock fixed across all installments
    if (fixedHomeInstallmentFeeStates[state] && policyType === PolicyType.Home) {
        downPayment = total / installmentCount;
    }
    else {
        const upfrontTotalFee = fees.reduce((acc, f) => acc + f.amount, 0);
        // otherwise, logic is more complex
        downPayment = getMinimumDownPayment({
            affinity,
            premium,
            policyType,
            state,
            quote,
            optionType,
            surplusContribution,
            upfrontFeeTotal: upfrontTotalFee
        });
        const effectiveDate = new Date(`${effectiveDateIn}T00:00:00`);
        console.log('[get down payment]  fee-utils');
        console.log(`downPayment is ${downPayment}`);
        console.log('----------');
        // make sure down payment is not more than 80% of the total premium
        if (downPayment > total * 0.8) {
            console.log(`[fee-utils] -> [getDownPayment] -> down payment of ${roundTwoDecimals(downPayment)} is > 80% of the total price of the policy, adjusting to ${roundTwoDecimals(total * 0.8)}`);
            downPayment = total * 0.8;
        }
        // The merge logic refers to this issue:
        // https://github.com/gobranch/branch/issues/3886
        if (getDate(effectiveDate) === billingDay || isMegaDownPay) {
            downPayment += (total - downPayment) / (isTwelveMonthPolicyTerm ? 11 : 5);
        }
    } // more complex installment-fee logic
    console.log(`downPayment with megadowpayment is ${downPayment}`);
    return roundTwoDecimals(downPayment);
}
//# sourceMappingURL=index.js.map